import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges
} from '@angular/core';
import { LoButton, LoCard, LoIcon, LoStatus } from 'lo-design-system';
import { PlanLadderSelectComponent } from './plan-ladder-select.component';
import { PlanCardLadderStop } from './plan-ladder.model';
import { planPriceFigure } from './plan-tier-copy';

export { PlanCardLadderStop } from './plan-ladder.model';

/** A single label/value row shown in the plan card body. */
export interface PlanCardRow {
	label: string;
	value: string;
}

/**
 * Presentational plan card shared by the settings plans page
 * (`ManageSubscriptionPlanComponent`) and the public/onboarding pricing page
 * (`PricingPageComponent`). Owns the card chrome and is driven entirely by
 * inputs; the parent keeps the data + action logic and reacts to {@link action}.
 *
 * SEVEN GRID ROWS, IN THIS ORDER, AND THAT IS A CONTRACT rather than a
 * layout preference: head · subtitle · price · price-note · dial · button ·
 * features. The head is the `lo-card` header; the other six are its children.
 * The plans grid lays the host out with `grid-template-rows: subgrid` and the
 * card's own `lo-card` passes the same rows down (see the stylesheet),
 * over exactly those seven rows, so every card's dial and every card's button
 * sit at the same height because they are literally in the same grid row — not
 * because a `min-height` happened to match the tallest subtitle. Add an eighth
 * child and it falls into an implicit row outside the span, landing on top of
 * whatever is below it; merge two and every card after this one shifts up a row.
 * `app-enterprise-card` carries the same seven for the same reason.
 *
 * The grid itself has EIGHT rows, and this card deliberately spans only seven:
 * the last one exists for `app-enterprise-card`'s longer bullet list, which
 * spans the final two. That is what lets a four-row card end where its content
 * ends instead of being stretched to the height of an eleven-bullet one — a
 * subgrid cannot be un-stretched in its subgridded axis, so not spanning the row
 * is the only lever. See `pricing-page.component.scss`.
 */
@Component({
	standalone: true,
	selector: 'app-plan-card',
	templateUrl: './plan-card.component.html',
	styleUrls: ['./plan-card.component.scss'],
	imports: [LoButton, LoCard, LoIcon, LoStatus, PlanLadderSelectComponent]
})
export class PlanCardComponent implements OnChanges {
	/**
	 * The heading, and it is the TIER — "Pro", not "Lolly Platform — Pro — 100
	 * credits/mo". The parent decides: both plans pages pass `planCardTitle()`,
	 * and a surface that genuinely needs the catalogue's per-rung name (the
	 * admin plans list, which is identifying rows rather than selling them)
	 * passes `plan.title` instead. A heading built from the rung would change
	 * under the reader every time the dial below it moved.
	 */
	@Input() title = '';
	/** The one line under the tier's name. Empty on a card that has none. */
	@Input() subtitle = '';
	/** Trial kind (`stripe` / `app`) — drives the trial badge colour. */
	@Input() trialKind?: string | null;
	/** Whether a free trial is actually available on this plan. */
	@Input() hasTrial = false;
	@Input() popular = false;
	/** What the recommendation pill says when {@link popular} is set. */
	@Input() popularLabel = 'Most chosen';

	/** Struck-through original price (annual savings); hidden when null. */
	@Input() struckPrice?: string | null;
	/** The figure, as the catalogue states it: a raw decimal string ("33.00").
	 *  The card renders {@link priceFigure}, which is this grouped and stripped
	 *  of cents it does not have. */
	@Input() priceValue = '';
	@Input() priceMeta = '';
	/** The note under the price ("No card, no expiry", "Billed annually — …").
	 *  Its row is rendered even when empty: an absent element would collapse the
	 *  row and take every card's dial off the shared line. */
	@Input() priceNote = '';

	/**
	 * The card's short list — the rows that settle which tier to buy. Built by
	 * `buildFeatureRows`, which is where the choice of WHICH rows and the reason
	 * for each one live; this input just prints them.
	 */
	@Input() featureRows: PlanCardRow[] = [];
	/**
	 * Whether this card offers the full "everything included" list. Off by
	 * default, so a surface that has nowhere to send the reader (the admin
	 * panel's plan preview, which has the editor beside it) shows no dead link.
	 */
	@Input() detailsAvailable = false;
	/** The reader asked for the full list. The PARENT opens it — the card stays
	 *  driven entirely by inputs. */
	@Output() detailsRequested = new EventEmitter<void>();

	/**
	 * The tier's allowance ladder. Empty (the default) on every card that has no
	 * rungs — a retired per-product plan, a card outside the application product
	 * — and those render the dial row as a spacer so they keep their place in
	 * the subgrid. A single stop renders as a disabled control stating the
	 * allowance: there is nothing to move, and an absent control would move the
	 * button.
	 */
	@Input() ladder: PlanCardLadderStop[] = [];
	/** Which stop is selected. Owned by the PARENT, which re-resolves the price,
	 *  the rows and the CTA for the chosen rung and pushes them back in. */
	@Input() ladderIndex = 0;
	/** The dial's own label, rendered above it and read out as its name. */
	@Input() ladderLabel = 'Monthly credits';
	/** The upsell under the rungs ("More than 1,200? Try Business"), built by
	 *  `ladderFooterNote`. Empty renders no line — which is every card whose
	 *  tier has nowhere to send the reader. */
	@Input() ladderFooter = '';
	@Output() ladderIndexChange = new EventEmitter<number>();

	@Input() actionLabel = '';
	@Input() actionDisabled = false;
	/** Drives the CTA visual variant + `data-action` hook. */
	@Input() actionType = 'subscribe';
	/** Show a leading check icon on the CTA (used for the "current" plan). */
	@Input() leadingCheck = false;
	/**
	 * What pressing the CTA will do, said before it is pressed — "Charged now,
	 * prorated" or "From Oct 1, 2026". Empty renders no line. It sits INSIDE the
	 * dial row, directly above the button, because an eighth top-level child
	 * would fall outside the subgrid span (see the class comment).
	 */
	@Input() actionNote = '';

	@Output() action = new EventEmitter<void>();

	/** The CTA's emphasis, resolved in `ngOnChanges`: the buying actions are
	 *  primary, everything that only reports a state is secondary. */
	actionVariant: 'primary' | 'secondary' = 'secondary';

	/** {@link ladderIndex} held inside the ladder's real bounds. Precomputed in
	 *  `ngOnChanges`, never derived in the template: Angular re-runs a template
	 *  getter on every change-detection pass. */
	ladderClampedIndex = 0;
	/** What a single-stop (or absent) ladder states in place of the control. */
	ladderStaticLabel = '';
	/**
	 * The price as the card prints it: grouped, and WITHOUT the cents when there
	 * are none. Every application price is a whole number of dollars, so `$33.00`
	 * was two characters of noise on the loudest thing on the card; a yearly
	 * rung's per-month figure does divide unevenly, and keeps its cents because
	 * rounding it would print a number nobody is charged.
	 *
	 * Computed here rather than by each caller so the four surfaces that render
	 * this card — both plans pages, the Social Listening onboarding page and the
	 * admin panel — cannot disagree about it.
	 */
	priceFigure = '';
	/** True when the dial is a real control rather than a fixed reading. */
	ladderSelectable = false;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['ladder'] || changes['ladderIndex']) {
			this.rebuildLadder();
		}
		if (changes['priceValue']) {
			this.priceFigure = planPriceFigure(this.priceValue);
		}
		if (changes['actionType'] || changes['popular']) {
			this.actionVariant =
				this.actionType === 'upgrade' ||
				(this.actionType === 'subscribe' && this.popular)
					? 'primary'
					: 'secondary';
		}
	}

	onLadderSelected(index: number): void {
		if (index === this.ladderIndex) {
			return;
		}
		this.ladderIndexChange.emit(index);
	}

	onAction(): void {
		if (this.actionDisabled) {
			return;
		}
		this.action.emit();
	}

	onDetails(): void {
		this.detailsRequested.emit();
	}

	private rebuildLadder(): void {
		this.ladderClampedIndex = Math.min(
			Math.max(this.ladderIndex, 0),
			Math.max(0, this.ladder.length - 1)
		);
		this.ladderSelectable = this.ladder.length > 1;
		this.ladderStaticLabel =
			this.ladder[this.ladderClampedIndex]?.label ?? '';
	}
}
